import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

interface Attachment {
  id: number
  file_name: string
  file_type: string
  title?: string | null
  description?: string | null
}

/**
 * Finds the organization's logo attachment and returns an authenticated
 * blob URL suitable for use in <img src>.
 *
 * Strategy: fetch all attachments (cached by react-query), find the one
 * titled "Organization Logo", then download its bytes through apiClient
 * so the JWT is included.
 */
export function useOrgLogo() {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  const { data: attachments } = useQuery<Attachment[]>({
    queryKey: ['attachments', 'org-logo-lookup'],
    queryFn: async () => {
      const { data } = await apiClient.get<Attachment[]>('/api/attachments')
      return data
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 min to avoid hammering on every nav
    refetchOnWindowFocus: false,
  })

  // Find the logo attachment by convention: title === 'Organization Logo'
  const logoAttachment = attachments?.find(
    (a) => a.title === 'Organization Logo'
  )

  useEffect(() => {
    if (!logoAttachment) {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
        setBlobUrl(null)
      }
      return
    }

    let cancelled = false

    const fetchBlob = async () => {
      try {
        const resp = await apiClient.get(
          `/api/attachments/${logoAttachment.id}/download`,
          { responseType: 'blob' },
        )
        if (cancelled) return
        const url = URL.createObjectURL(resp.data)
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return url
        })
      } catch {
        // Download failed — no logo to show
      }
    }

    fetchBlob()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoAttachment?.id])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    logoUrl: blobUrl,
    isLoading: !attachments,
    attachmentId: logoAttachment?.id ?? null,
  }
}
